// src/components/laboratory/modals/ProductDetailModal.jsx
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { getProductImageUrl } from '../../../utils/imageUrl';
import { 
  Info, 
  Tag, 
  Megaphone, 
  BarChart3, 
  Package, 
  Calendar,
  DollarSign,
  Percent,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Eye,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ModalComponent from '../../common/ModalComponent';
import { GET_PROMOTIONS_BY_LABORATORY } from '../../../api/laboratory-api';

const ProductDetailModal = ({ 
  product, 
  isOpen, 
  onClose, 
  laboratory,
  promotions = [],
  adds = [] // This will come from the parent component
}) => {
  const [activeTab, setActiveTab] = useState('general');

  // Filter promotions that include this product
  const productPromotions = promotions.filter(promotion => 
    promotion.products && promotion.products.some(p => p.id === product?.id)
  );

  // Filter adds that include this product
  const productAdds = adds.filter(add => add.productId === product?.id);

  // Get status display for promotions
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'IN_PROGRESS':
        return {
          text: 'En cours',
          className: 'bg-green-100 text-green-800',
          icon: CheckCircle
        };
      case 'DRAFT':
        return {
          text: 'Brouillon',
          className: 'bg-yellow-100 text-yellow-800',
          icon: Clock
        };
      case 'CLOSED':
        return {
          text: 'Terminée',
          className: 'bg-red-100 text-red-800',
          icon: XCircle
        };
      default:
        return {
          text: status,
          className: 'bg-gray-100 text-gray-800',
          icon: Info
        };
    }
  };

  // Check if promotion is currently active
  const isPromotionActive = (promotion) => {
    const now = new Date();
    const startDate = new Date(promotion.startDate);
    const endDate = new Date(promotion.endDate);
    return now >= startDate && now <= endDate && promotion.status === 'IN_PROGRESS';
  };

  // Safe date format helper — returns '—' if the date is missing or invalid
  const safeFormat = (dateStr, fmt, opts) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return format(d, fmt, opts);
  };

  // Check if add is currently active
  const isAddActive = (add) => {
    const now = new Date();
    const startDate = new Date(add.dateDebut);
    const endDate = new Date(add.dateFin);
    return now >= startDate && now <= endDate;
  };

  // Render General tab
  const renderGeneralTab = () => {
    if (!product) return null;

    return (
      <div className="space-y-6">
        {/* Product Images */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Main Image */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Image principale</h4>
            {product.image ? (
              <img
                src={getProductImageUrl(product.image)}
                alt={product.name}
                className="w-full h-64 object-cover rounded-lg border border-gray-200"
              />
            ) : (
              <div className="w-full h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                <Package className="h-16 w-16 text-gray-400" />
              </div>
            )}
          </div>

          {/* Additional Images */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Images supplémentaires</h4>
            <div className="grid grid-cols-2 gap-2">
              {[product.images, product.imagethree, product.quatriemimage, product.cinquiemimage]
                .filter(Boolean)
                .map((imageUrl, index) => (
                  <img
                    key={index}
                    src={getProductImageUrl(imageUrl)}
                    alt={`${product.name} - Image ${index + 2}`}
                    className="w-full h-24 object-cover rounded-md border border-gray-200"
                  />
                ))}
              {[product.images, product.imagethree, product.quatriemimage, product.cinquiemimage]
                .filter(Boolean).length === 0 && (
                <div className="col-span-2 text-center py-8 text-gray-500">
                  <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Aucune image supplémentaire</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Product Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Informations du produit</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Nom du produit</label>
              <p className="mt-1 text-sm text-gray-900 font-medium">{product.name}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500">Prix public (DH)</label>
              <p className="mt-1 text-sm text-gray-900 font-semibold text-green-600">
                {product.price?.toFixed(2)} DH
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Prix PGHT (DH)</label>
              <p className="mt-1 text-sm text-gray-900 font-semibold text-blue-600">
                {product.prixPGHT != null ? product.prixPGHT.toFixed(2) + ' DH' : '—'}
              </p>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500">Description</label>
              <p className="mt-1 text-sm text-gray-900 leading-relaxed">{product.description}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500">Date de création</label>
              <p className="mt-1 text-sm text-gray-900">
                {safeFormat(product.createdAt, 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Dernière mise à jour</label>
              <p className="mt-1 text-sm text-gray-900">
                {safeFormat(product.updatedAt, 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </p>
            </div>
          </div>
        </div>

        {/* Video Link */}
        {product.videoLink && (
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Vidéo de présentation</h4>
            <a
              href={product.videoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Voir la vidéo
            </a>
          </div>
        )}

        {/* Laboratory Information */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h4 className="text-sm font-medium text-green-800 mb-2">Laboratoire</h4>
          <p className="text-sm text-green-700">{laboratory?.nom}</p>
        </div>
      </div>
    );
  };

  // Render Promotions tab
  const renderPromotionsTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-medium text-gray-900">
            Promotions associées ({productPromotions.length})
          </h4>
        </div>

        {productPromotions.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-2">Aucune promotion</h3>
            <p className="text-sm text-gray-500">
              Ce produit n'est associé à aucune promotion pour le moment.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {productPromotions.map((promotion) => {
              const statusDisplay = getStatusDisplay(promotion.status);
              const StatusIcon = statusDisplay.icon;
              const isActive = isPromotionActive(promotion);

              return (
                <div
                  key={promotion.id}
                  className={`border rounded-lg p-4 ${
                    isActive ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h5 className="text-base font-medium text-gray-900">{promotion.name}</h5>
                        {isActive && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Actif maintenant
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>
                            {format(new Date(promotion.startDate), 'dd/MM/yyyy', { locale: fr })} - {' '}
                            {format(new Date(promotion.endDate), 'dd/MM/yyyy', { locale: fr })}
                          </span>
                        </div>
                        
                        {promotion.quantity && (
                          <div className="flex items-center">
                            <Tag className="h-4 w-4 mr-1" />
                            <span>{promotion.quantity} codes QR</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <StatusIcon className="h-4 w-4 text-gray-400" />
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusDisplay.className}`}>
                        {statusDisplay.text}
                      </span>
                    </div>
                  </div>

                  {/* Commission Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-white p-3 rounded border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 text-green-500 mr-2" />
                          <span className="text-sm font-medium text-gray-700">Commission Vendeur</span>
                        </div>
                        <span className="text-sm font-bold text-green-700">
                          {promotion.commissionAmount} DH
                        </span>
                      </div>
                    </div>

                    <div className="bg-white p-3 rounded border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Percent className="h-4 w-4 text-blue-500 mr-2" />
                          <span className="text-sm font-medium text-gray-700">Commission Mazid</span>
                        </div>
                        <span className="text-sm font-bold text-blue-700">
                          {promotion.commissionPercentage}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Other products in this promotion */}
                  {promotion.products && promotion.products.length > 1 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">
                        Autres produits dans cette promotion :
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {promotion.products
                          .filter(p => p.id !== product.id)
                          .slice(0, 3)
                          .map(otherProduct => (
                          <span
                            key={otherProduct.id}
                            className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                          >
                            {otherProduct.name}
                          </span>
                        ))}
                        {promotion.products.filter(p => p.id !== product.id).length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded">
                            +{promotion.products.filter(p => p.id !== product.id).length - 3} autres
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render Publicité (Ads) tab
  const renderPubliciteTab = () => {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-medium text-gray-900">
            Publicités associées ({productAdds.length})
          </h4>
        </div>

        {productAdds.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-2">Aucune publicité</h3>
            <p className="text-sm text-gray-500">
              Ce produit n'a pas encore de publicités associées.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {productAdds.map((add) => {
              const isActive = isAddActive(add);
              const now = new Date();
              const startDate = new Date(add.dateDebut);
              const endDate = new Date(add.dateFin);
              const isExpired = now > endDate;
              const isPending = now < startDate;

              return (
                <div
                  key={add.id}
                  className={`border rounded-lg p-4 ${
                    isActive ? 'border-blue-300 bg-blue-50' : 
                    isExpired ? 'border-red-200 bg-red-50' :
                    'border-yellow-200 bg-yellow-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Megaphone className="h-5 w-5 text-gray-600" />
                        <h5 className="text-base font-medium text-gray-900">
                          Publicité - {product.name}
                        </h5>
                        
                        {isActive && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            En cours
                          </span>
                        )}
                        
                        {isExpired && (
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            Expirée
                          </span>
                        )}
                        
                        {isPending && (
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            À venir
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>
                            Début: {format(startDate, 'dd/MM/yyyy', { locale: fr })}
                          </span>
                        </div>
                        
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>
                            Fin: {format(endDate, 'dd/MM/yyyy', { locale: fr })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add duration info */}
                  <div className="bg-white p-3 rounded border mt-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 text-purple-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Durée de diffusion</span>
                      </div>
                      <span className="text-sm font-medium text-purple-700">
                        {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} jours
                      </span>
                    </div>
                  </div>

                  {/* Laboratory info */}
                  {add.laboratory && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">
                        Laboratoire: <span className="font-medium">{add.laboratory.nom}</span>
                      </p>
                    </div>
                  )}

                  {/* Creation info */}
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      Créée le {format(new Date(add.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render Statistiques tab
  const renderStatistiquesTab = () => {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <BarChart3 size={64} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Statistiques en construction</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Les statistiques détaillées de ce produit seront bientôt disponibles. 
            Vous pourrez y voir les performances, les ventes et l'analyse des données.
          </p>
          <div className="mt-6 bg-blue-50 p-4 rounded-lg max-w-md mx-auto">
            <h4 className="font-medium text-blue-800 mb-2">Fonctionnalités prévues :</h4>
            <ul className="text-sm text-blue-700 space-y-1 text-left">
              <li>• Nombre de scans par produit</li>
              <li>• Revenus générés par le produit</li>
              <li>• Performance des promotions associées</li>
              <li>• Analyse géographique des ventes</li>
              <li>• Tendances temporelles</li>
              <li>• Comparaison avec d'autres produits</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  if (!product) return null;

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={`Détails du produit: ${product.name}`}
      size="xl"
      showFooter={false}
    >
      <div className="space-y-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Info className="inline-block mr-1 h-4 w-4" />
              Général
            </button>
            
            <button
              onClick={() => setActiveTab('promotions')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'promotions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Tag className="inline-block mr-1 h-4 w-4" />
              Promotions ({productPromotions.length})
            </button>
            
            <button
              onClick={() => setActiveTab('publicite')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'publicite'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Megaphone className="inline-block mr-1 h-4 w-4" />
              Publicité ({productAdds.length})
            </button>
            
            <button
              onClick={() => setActiveTab('statistiques')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'statistiques'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="inline-block mr-1 h-4 w-4" />
              Statistiques
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {activeTab === 'general' && renderGeneralTab()}
          {activeTab === 'promotions' && renderPromotionsTab()}
          {activeTab === 'publicite' && renderPubliciteTab()}
          {activeTab === 'statistiques' && renderStatistiquesTab()}
        </div>
      </div>
    </ModalComponent>
  );
};

export default ProductDetailModal;